/**
 * Notification bell icon with unread badge.
 * Toggles the NotificationPanel dropdown on click.
 * Animates when new notifications arrive.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@/hooks/useNotifications';
import { IconBell } from '@/components/icons/Icons';
import { Badge } from '@/components/common/Badge';
import { NotificationPanel } from './NotificationPanel';
import { cn } from '@/lib/utils';
import styles from './NotificationBell.module.css';

export function NotificationBell() {
  const { t } = useTranslation('settings');
  const { unreadCount } = useNotifications();
  const [panelOpen, setPanelOpen] = useState(false);
  const [animate, setAnimate] = useState(false);
  const prevCountRef = useRef(unreadCount);

  // Trigger bell animation when unread count increases
  useEffect(() => {
    if (unreadCount > prevCountRef.current) {
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 600);
      return () => clearTimeout(timer);
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount]);

  const togglePanel = useCallback(() => {
    setPanelOpen((prev) => !prev);
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);

  return (
    <div className={styles.wrapper}>
      <button
        className={cn(styles.bellButton, animate && styles.bellRing)}
        onClick={togglePanel}
        aria-label={t('notifications')}
        aria-expanded={panelOpen}
        type="button"
      >
        <Badge count={unreadCount} color="error">
          <IconBell />
        </Badge>
      </button>

      <NotificationPanel open={panelOpen} onClose={closePanel} />
    </div>
  );
}
